import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { addDays, eachDayOfInterval, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns';

const ScheduleCalendar = ({ viewMode, dateRange, rtl, shifts, onEditShift, onCreateShift, onDeleteShift }) => {
  const { t } = useTranslation();

  const arrow = rtl ? '←' : '→';

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':');
    if (parts.length < 2) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  };

  const formatMinutesToTime = (minsTotal) => {
    let m = Number(minsTotal);
    if (!Number.isFinite(m)) return '';
    m = ((m % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  };

  const getBreakRulesForTile = (shift) => {
    // Avoid showing break rules on the overnight continuation tile to reduce confusion.
    if (shift?.__overnightSegment === 'end') return null;

    const base = shift?.__sourceShift || shift;
    const startMin = parseTimeToMinutes(base?.startTime);
    const endMin = parseTimeToMinutes(base?.endTime);
    if (startMin === null || endMin === null) return null;

    const overnight = endMin <= startMin;
    const shiftEndCalc = overnight ? (endMin + 1440) : endMin;

    const adjust = (m) => {
      if (m === null) return null;
      let v = m;
      if (overnight && v < startMin) v += 1440;
      return v;
    };

    const breakStartMinRaw = parseTimeToMinutes(base?.breakStartTime);
    const breakEndMinRaw = parseTimeToMinutes(base?.breakEndTime);
    const breakStartCalc = adjust(breakStartMinRaw);
    const breakEndCalc = adjust(breakEndMinRaw);

    const durationRaw = (base?.breakDurationMinutes != null && Number(base.breakDurationMinutes) > 0)
      ? Number(base.breakDurationMinutes)
      : null;

    const hasAnyBreakConfig = breakStartCalc !== null || breakEndCalc !== null || durationRaw !== null;
    if (!hasAnyBreakConfig) {
      return { summary: t('BreakNone', { defaultValue: 'Break: none' }) };
    }

    const windowStartCalc = breakStartCalc ?? startMin;
    const windowEndCalc = breakEndCalc ?? shiftEndCalc;

    let durationEffective = durationRaw;
    let inferred = false;
    if ((durationEffective == null || durationEffective <= 0) && breakStartCalc != null && breakEndCalc != null) {
      const inferredDur = Math.floor(windowEndCalc - windowStartCalc);
      if (inferredDur > 0) {
        durationEffective = inferredDur;
        inferred = true;
      }
    }

    if (durationEffective == null || durationEffective <= 0) {
      return { summary: t('BreakNotConfiguredDurationMissing', { defaultValue: 'Break: not configured (duration missing)' }) };
    }


    const windowLabel = (breakStartMinRaw !== null || breakEndMinRaw !== null)
      ? `${formatMinutesToTime(windowStartCalc)} – ${formatMinutesToTime(windowEndCalc)}`
      : t('during shift', { defaultValue: 'during shift' });

    const durationLabel = inferred
      ? `${durationEffective} min (${t('inferred', { defaultValue: 'inferred' })})`
      : `${durationEffective} min`;

    return {
      summary: windowLabel,
      details: `${t('Duration', { defaultValue: 'Duration' })}: ${durationLabel}`,
    };
  };

  const isOvernightShift = (shift) => {
    const start = parseTimeToMinutes(shift?.startTime);
    const end = parseTimeToMinutes(shift?.endTime);
    if (start === null || end === null) return false;
    // If end <= start, it crosses midnight (e.g., 22:00 -> 06:00)
    return end <= start;
  };

  const expandedShifts = useMemo(() => {
    const result = [];
    (shifts || []).forEach((shift) => {
      if (!shift?.date || !shift?.startTime || !shift?.endTime || !isOvernightShift(shift)) {
        result.push(shift);
        return;
      }

      const nextDayKey = format(addDays(new Date(`${shift.date}T00:00:00`), 1), 'yyyy-MM-dd');
      const sourceKey = shift?.id ?? `${shift?.date}-${shift?.startTime}-${shift?.endTime}-${shift?.origin || ''}`;

      result.push({
        ...shift,
        __sourceShift: shift,
        __overnightSegment: 'start',
        __overnightStartTime: shift.startTime,
        __overnightEndTime: shift.endTime,
        __segmentKey: `${sourceKey}-overnight-start`,
      });

      result.push({
        ...shift,
        date: nextDayKey,
        __sourceShift: shift,
        __overnightSegment: 'end',
        __overnightStartTime: shift.startTime,
        __overnightEndTime: shift.endTime,
        __segmentKey: `${sourceKey}-overnight-end`,
      });
    });
    return result;
  }, [shifts]);

  const shiftsByDate = useMemo(() => {
    const map = new Map();
    expandedShifts.forEach((shift) => {
      const key = shift?.date;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(shift);
    });

    // Sort within each day by display start time
    map.forEach((arr) => {
      arr.sort((a, b) => {
        const aStart = a?.__overnightSegment === 'end' ? 0 : (parseTimeToMinutes(a?.startTime) ?? 0);
        const bStart = b?.__overnightSegment === 'end' ? 0 : (parseTimeToMinutes(b?.startTime) ?? 0);
        return aStart - bStart;
      });
    });

    return map;
  }, [expandedShifts]);

  const getShiftsForDate = (date) => {
    const key = format(date, 'yyyy-MM-dd');
    return shiftsByDate.get(key) || [];
  };

  const getTimeRangeLabel = (shift) => {
    if (shift?.__overnightSegment === 'start') {
      return `${shift?.__overnightStartTime} ${arrow} ${shift?.__overnightEndTime}`;
    }
    if (shift?.__overnightSegment === 'end') {
      return `${arrow} ${shift?.__overnightEndTime}`;
    }
    return `${shift?.startTime} - ${shift?.endTime}`;
  };

  const getCompactTimeLabel = (shift) => {
    if (shift?.__overnightSegment === 'start') return `${shift?.__overnightStartTime}${arrow}`;
    if (shift?.__overnightSegment === 'end') return `${arrow}${shift?.__overnightEndTime}`;
    return shift?.startTime;
  };

  const getOriginBadge = (shift) => {
    // Backend returns origin as "Rule", "ShiftSetEmbedded", or "ManualOverride"
    if (shift?.origin === 'Rule') {
      return (
        <div className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
          <span>🔄</span>
          <span>{t('Recurring')}</span>
        </div>
      );
    } else if (shift?.origin === 'ShiftSetEmbedded') {
      return (
        <div className="flex items-center gap-1 text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
          <span>📋</span>
        </div>
      );
    } else if (shift?.origin === 'Manual' || shift?.origin === 'ManualOverride') {
      return (
        <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">
          <span>✏️</span>
          <span>{t('One-Time')}</span>
        </div>
      );
    }
    return null;
  };

  // Display user info - shows count if multiple users, or single user full name
  const getUserDisplay = (shift) => {
    // ShiftSet occurrences should display the template name (single entry per day/time-slot).
    if (shift?.shiftSetName && (shift?.shiftSetId || shift?.origin === 'Rule' || shift?.origin === 'ShiftSetEmbedded')) {
      return shift.shiftSetName;
    }
    // Prefer explicit first name if provided by adapter
    if (shift?.userFirstName && typeof shift.userFirstName === 'string' && shift.userFirstName.trim().length > 0) {
      return shift.userFirstName.trim();
    }
    const objName = (typeof shift?.userName === 'object') ? (shift?.userName?.fullName || shift?.userName?.name || '') : '';
    const rawName = (typeof shift?.userName === 'string') ? shift?.userName : '';
    const fullName = shift?.userFullName || objName || rawName || shift?.assignedUsers?.[0]?.fullName || shift?.assignedUsers?.[0]?.name || '';
    if (!fullName || fullName.trim().length === 0) return '—';
    // Extract first token for single-user display
    const first = fullName.trim().split(/\s+/)[0];
    return first;
  };

  const canDelete = (shift) => {
    // Avoid deleting aggregated template items (they may represent many users/IDs)
    if (shift?.isAggregated) return false;
    if (shift?.userCount && shift.userCount > 1) return false;
    return !shift?.isVirtual && shift?.origin !== 'Rule' && shift?.origin !== 'ShiftSetEmbedded';
  };

  const renderWeekView = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return (
      <div className="p-6 overflow-x-auto">
        <div className="grid grid-cols-7 gap-4 min-w-[700px]">
          {/* Day Headers */}
          {weekDays?.map((day, index) => (
            <div key={day} className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">{day}</div>
              <div className="text-xs text-muted-foreground">
                {format(dateRange?.[index], 'd MMM')}
              </div>
            </div>
          ))}

          {/* Day Cells */}
          {dateRange?.map((date, index) => {
            const dayShifts = getShiftsForDate(date);
            const isToday = isSameDay(date, new Date());

            return (
              <div
                key={index}
                className={`min-h-[200px] border rounded-lg p-3 ${
                  isToday ? 'border-primary border-2 bg-primary/5' : 'border-border bg-card'
                }`}
              >
                <div className="space-y-2">
                  {dayShifts?.length === 0 ? (
                    <button
                      onClick={() => onCreateShift?.(date)}
                      className="w-full py-2 border-2 border-dashed border-border rounded text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors text-sm"
                    >
                      <Icon name="Plus" size={16} className="mx-auto" />
                    </button>
                  ) : (
                    dayShifts?.map((shift, shiftIndex) => (
                      <div
                        key={shift?.__segmentKey || `${shift?.shiftSetId || ''}-${shift?.recurringShiftRuleId || ''}-${shift?.origin || ''}-${shift?.date}-${shift?.startTime}-${shift?.endTime}-${shift?.id || shiftIndex}`}
                        onClick={() => onEditShift?.(shift?.__sourceShift || shift)}
                        className={`p-2 rounded cursor-pointer transition-all ${
                          shift?.isVirtual
                            ? 'border-2 border-dashed border-border bg-muted' :'border border-border bg-card hover:shadow-md'
                        }`}
                        style={{
                          borderInlineStartWidth: '4px',
                          borderInlineStartColor: shift?.color
                        }}
                        title={shift?.__overnightSegment ? `${t('Overnight shift')} (${shift?.__overnightStartTime} → ${shift?.__overnightEndTime})` : undefined}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="text-xs font-medium text-foreground">
                            {getUserDisplay(shift)}
                          </div>
                          {canDelete(shift) && (
                            <button
                              className="text-muted-foreground hover:text-destructive"
                              title={`${t('Delete')} ${t('Shift')}`}
                              onClick={(e) => { e.stopPropagation(); onDeleteShift?.(shift?.__sourceShift || shift); }}
                            >
                              <Icon name="Trash" size={14} />
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">
                          {getTimeRangeLabel(shift)}
                        </div>
                        {shift?.__overnightSegment && (
                          <div className="text-[10px] text-muted-foreground mb-1">
                            {shift?.__overnightSegment === 'end' ? t('Overnight (cont.)') : t('Overnight')}
                          </div>
                        )}
                        {(() => {
                          const br = getBreakRulesForTile(shift);
                          if (!br?.summary) return null;
                          return (
                            <div className="mb-1 border border-border rounded bg-muted px-2 py-1">
                              <div className="text-[11px] font-semibold text-foreground">{t('Break', { defaultValue: 'Break' })}</div>
                              <div className="text-xs text-muted-foreground">{br.summary}</div>
                              {br.details ? (
                                <div className="text-[11px] text-muted-foreground">{br.details}</div>
                              ) : null}
                            </div>
                          );
                        })()}
                        {getOriginBadge(shift)}
                        {shift?.isVirtual && (
                          <div className="mt-1 text-xs text-muted-foreground italic">{t('Virtual')}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const monthStart = dateRange?.[0] || null;
    const monthEnd = dateRange?.[dateRange?.length - 1] || null;
    const baseMonth = (dateRange || []).find((d) => d && d.getDate && d.getDate() === 1) || monthStart;

    const gridDays = (monthStart && monthEnd)
      ? eachDayOfInterval({
        start: startOfWeek(monthStart, { weekStartsOn: 1 }),
        end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
      })
      : [];

    const weeks = [];
    for (let i = 0; i < gridDays.length; i += 7) {
      weeks.push(gridDays.slice(i, i + 7));
    }

    return (
      <div className="p-6 overflow-x-auto">
        <div className="grid grid-cols-7 gap-2 mb-2 min-w-[700px]">
          {weekDays?.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {weeks?.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-2">
              {week?.map((date, dayIndex) => {
                const dayShifts = getShiftsForDate(date);
                const isToday = isSameDay(date, new Date());
                const isOutsideMonth = baseMonth
                  ? (date.getMonth() !== baseMonth.getMonth() || date.getFullYear() !== baseMonth.getFullYear())
                  : false;

                return (
                  <div
                    key={dayIndex}
                    className={`min-h-[120px] border rounded p-2 ${
                      isToday ? 'border-primary border-2 bg-primary/5' : 'border-border bg-card'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isOutsideMonth ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {format(date, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayShifts?.slice(0, 2)?.map((shift, shiftIndex) => (
                        <div
                          key={shift?.__segmentKey || `${shift?.shiftSetId || ''}-${shift?.recurringShiftRuleId || ''}-${shift?.origin || ''}-${shift?.date}-${shift?.startTime}-${shift?.endTime}-${shift?.id || shiftIndex}`}
                          onClick={() => onEditShift?.(shift?.__sourceShift || shift)}
                          className={`text-xs p-1 rounded cursor-pointer ${
                            shift?.isVirtual
                              ? 'border border-dashed border-border bg-muted' :'bg-primary/10 text-primary'
                          }`}
                          title={shift?.__overnightSegment ? `Overnight shift (${shift?.__overnightStartTime} → ${shift?.__overnightEndTime})` : undefined}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium truncate">
                              {getUserDisplay(shift)}
                            </div>
                            {canDelete(shift) && (
                              <button
                                className="text-muted-foreground hover:text-destructive ms-2"
                                title={`${t('Delete')} ${t('Shift')}`}
                                onClick={(e) => { e.stopPropagation(); onDeleteShift?.(shift?.__sourceShift || shift); }}
                              >
                                <Icon name="Trash" size={12} />
                              </button>
                            )}
                          </div>
                          <div className="text-xs">{getCompactTimeLabel(shift)}</div>
                        </div>
                      ))}
                      {dayShifts?.length > 2 && (
                        <div className="text-xs text-muted-foreground text-center">
                          +{dayShifts?.length - 2} {t('more')}
                        </div>
                      )}
                      {dayShifts?.length === 0 && (
                        <button
                          onClick={() => onCreateShift?.(date)}
                          className="w-full py-1 border border-dashed border-border rounded text-muted-foreground hover:border-primary hover:text-primary text-xs"
                        >
                          <Icon name="Plus" size={12} className="mx-auto" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return viewMode === 'week' ? renderWeekView() : renderMonthView();
};

export default ScheduleCalendar;